import dagre from 'dagre';
import { FlowNode, FlowEdge } from '../types/zapier';

export interface LayoutConfig {
  nodeWidth?: number;
  nodeHeight?: number;
  rankSep?: number; // Vertical spacing between ranks
  nodeSep?: number; // Horizontal spacing between nodes
  edgeSep?: number; // Spacing between edges
  rankDir?: 'TB' | 'BT' | 'LR' | 'RL'; // Direction of layout
}

const DEFAULT_CONFIG: Required<LayoutConfig> = {
  nodeWidth: 320,
  nodeHeight: 80,
  rankSep: 120,
  nodeSep: 80,
  edgeSep: 20,
  rankDir: 'TB',
};

/**
 * Detects if a workflow has branching (any node with multiple children)
 */
export function hasBranching(edges: FlowEdge[]): boolean {
  const childrenCount = new Map<string, number>();

  edges.forEach(edge => {
    const count = childrenCount.get(edge.source) || 0;
    childrenCount.set(edge.source, count + 1);
  });

  return Array.from(childrenCount.values()).some(count => count > 1);
}

/**
 * Uses dagre to calculate optimal positions for nodes with branching support
 */
export function calculateLayout(
  nodes: FlowNode[],
  edges: FlowEdge[],
  config: LayoutConfig = {}
): FlowNode[] {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  // Create a new directed graph
  const dagreGraph = new dagre.graphlib.Graph();

  // Set graph configuration
  dagreGraph.setGraph({
    rankdir: finalConfig.rankDir,
    nodesep: finalConfig.nodeSep,
    ranksep: finalConfig.rankSep,
    edgesep: finalConfig.edgeSep,
    marginx: 50,
    marginy: 50,
  });

  // Default to assigning a new object as a label for each new edge
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Add nodes to the graph
  nodes.forEach(node => {
    dagreGraph.setNode(node.id, {
      width: finalConfig.nodeWidth,
      height: finalConfig.nodeHeight,
      data: node.data,
    });
  });

  // Add edges to the graph
  edges.forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate the layout
  dagre.layout(dagreGraph);

  // Apply the calculated positions to nodes
  const layoutedNodes: FlowNode[] = nodes.map(node => {
    const nodeWithPosition = dagreGraph.node(node.id);

    return {
      ...node,
      position: {
        // Dagre positions are center-based, React Flow uses top-left
        x: nodeWithPosition.x - finalConfig.nodeWidth / 2,
        y: nodeWithPosition.y - finalConfig.nodeHeight / 2,
      },
    };
  });

  return layoutedNodes;
}

/**
 * Detects branch nodes and returns metadata about branching points
 */
export function analyzeBranching(edges: FlowEdge[]): Map<string, {
  branchNodeId: string;
  childrenIds: string[];
  branchCount: number;
}> {
  const branchingInfo = new Map<string, {
    branchNodeId: string;
    childrenIds: string[];
    branchCount: number;
  }>();

  const childrenMap = new Map<string, string[]>();

  // Build children map
  edges.forEach(edge => {
    const children = childrenMap.get(edge.source) || [];
    children.push(edge.target);
    childrenMap.set(edge.source, children);
  });

  // Find nodes with multiple children (branching points)
  childrenMap.forEach((children, nodeId) => {
    if (children.length > 1) {
      branchingInfo.set(nodeId, {
        branchNodeId: nodeId,
        childrenIds: children,
        branchCount: children.length,
      });
    }
  });

  return branchingInfo;
}

/**
 * Enhances edges with visual indicators for branches
 * Returns edges with labels for branch paths if metadata is available
 */
export function enhanceEdgesForBranching(
  edges: FlowEdge[],
  nodes: FlowNode[]
): FlowEdge[] {
  const branchingInfo = analyzeBranching(edges);

  return edges.map(edge => {
    const branchInfo = branchingInfo.get(edge.source);

    if (branchInfo && branchInfo.branchCount > 1) {
      // This is an edge from a branching node
      const branchIndex = branchInfo.childrenIds.indexOf(edge.target);
      const sourceNode = nodes.find(n => n.id === edge.source);

      // Try to find condition information from the node data
      let label = `Path ${branchIndex + 1}`;

      // Check if the source node has branch condition data
      if (sourceNode?.data.nodeType === 'branch') {
        const rawNode = sourceNode.data.rawNode as any;

        // Look for condition data in params or meta
        if (rawNode.params?.paths && Array.isArray(rawNode.params.paths)) {
          const pathData = rawNode.params.paths[branchIndex];
          if (pathData?.label || pathData?.name) {
            label = pathData.label || pathData.name;
          }
        }
      }

      return {
        ...edge,
        label,
        labelStyle: {
          fill: '#6b7280',
          fontWeight: 500,
          fontSize: 12,
        },
        labelBgStyle: {
          fill: '#f9fafb',
          fillOpacity: 0.9,
        },
        labelBgPadding: [8, 4] as [number, number],
        labelBgBorderRadius: 4,
        style: {
          ...edge.style,
          strokeWidth: 2,
          stroke: '#ec4899', // Pink for branch edges
        },
      };
    }

    return edge;
  });
}
